import type { FieldError } from '@onefootprint/hooks';
import type { VaultData } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { retryOnConnectionError } from '@/idv2/components/errors/get-error-key';
import { CollectCustomData } from '@/idv2/features/collect-custom-data';
import { getCustomDataDis } from '@/idv2/features/collect-custom-data/utils/get-custom-data-dis';
import { getDecryptOptions, getPatchVaultMutation } from '@/idv2/features/collect-custom-data/utils/vault-operations';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { useVaultMutationError } from '@/idv2/hooks/use-vault-mutation-error';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn,
  logError
} = getLogger({
  name: 'idv2',
  location: 'collect-custom-data-route'
});
export const Route = createFileRoute('/onboarding/$token/collect-custom-data')({
  component: CollectCustomDataPage,
  loader: async ({
    params: {
      token
    },
    context: {
      queryClient,
      currentRequirement
    }
  }) => {
    if (currentRequirement?.kind !== 'collect_custom_data') {
      throw new Error(`Invalid requirement kind: ${currentRequirement?.kind ?? 'undefined'}`);
    }
    const fields = getCustomDataDis(currentRequirement);
    logInfo('Collect custom data loader started', {
      requirementKind: currentRequirement.kind,
      header: currentRequirement.page.header,
      fields: fields.join(',')
    });
    const decryptedVault = await queryClient.fetchQuery({
      ...getDecryptOptions(currentRequirement.subject, {
        headers: {
          'X-Fp-Authorization': token
        },
        body: {
          fields
        }
      }),
      retry: retryOnConnectionError
    });
    logInfo('Collect custom data loader completed successfully');
    return {
      currentRequirement,
      decryptedVault
    };
  }
});
export function CollectCustomDataPage() {
  const {
    currentRequirement,
    decryptedVault
  } = Route.useLoaderData();
  const {
    token
  } = Route.useParams();
  const footprint = useFootprint();
  const {
    l10n,
    config
  } = Route.useRouteContext();
  const handleVaultError = useVaultMutationError();
  const disableSsnValidation = !config.isLive;
  const {
    mutateAsync: vaultMutation,
    isPending: isVaultMutationPending
  } = useMutation(getPatchVaultMutation(currentRequirement.subject, {
    headers: {
      'X-Fp-Authorization': token
    }
  }));
  // The save finishes before the next step loads, and a second submit in between would save twice.
  const [isNavigating, setIsNavigating] = useState(false);
  const onSubmit = async (data: VaultData, onError: (errors: FieldError[]) => void, pressedIndex?: number) => {
    logInfo('Collect custom data form submission started', {
      vaultKeys: Object.keys(data).join(',')
    });
    try {
      await vaultMutation({
        body: data
      });
      logInfo('Collect custom data vault mutation succeeded');
      const {
        nodeId
      } = currentRequirement;
      const clickedButton = nodeId && pressedIndex !== undefined ? {
        nodeId,
        index: pressedIndex
      } : undefined;
      setIsNavigating(true);
      await footprint.navigation.forward({
        clickedButton
      });
    } catch (error) {
      const fieldErrors = handleVaultError(error, onError);
      const log = fieldErrors.length > 0 ? logWarn : logError;
      log('Collect custom data vault mutation failed', error, {
        fieldErrorCount: fieldErrors.length
      });
    } finally {
      setIsNavigating(false);
    }
  };
  return <CollectCustomData l10n={l10n} isLoading={isVaultMutationPending || isNavigating} onSubmit={onSubmit} decryptedVault={decryptedVault} currentRequirement={currentRequirement} disableSsnValidation={disableSsnValidation} />;
}